import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PTORequestModal = ({ isOpen, onClose, onSuccess, currentUser }) => {
  const [formData, setFormData] = useState({
    pto_type: 'vacation',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    total_hours: '',
    reason: '',
    notes: ''
  });
  
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [calculatedHours, setCalculatedHours] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchBalance();
      // Reset form
      setFormData({
        pto_type: 'vacation',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        total_hours: '',
        reason: '',
        notes: ''
      });
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    // Auto-calculate hours when dates change
    if (formData.start_date && formData.end_date) {
      calculateHours();
    }
  }, [formData.start_date, formData.end_date, formData.start_time, formData.end_time]);

  const fetchBalance = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/pto/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalance(res.data.balance);
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const calculateHours = () => {
    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    
    if (start && end && start <= end) {
      // If time is specified, calculate exact hours
      if (formData.start_time && formData.end_time) {
        const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
        const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
        const diffMs = endDateTime - startDateTime;
        const hours = diffMs / (1000 * 60 * 60);
        setCalculatedHours(hours);
        setFormData(prev => ({ ...prev, total_hours: hours.toFixed(2) }));
      } else {
        // Calculate business days * 8 hours
        let days = 0;
        const currentDate = new Date(start);
        
        while (currentDate <= end) {
          const dayOfWeek = currentDate.getDay();
          // Count weekdays only (Monday = 1, Friday = 5)
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            days++;
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        const hours = days * 8;
        setCalculatedHours(hours);
        setFormData(prev => ({ ...prev, total_hours: hours.toString() }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.start_date || !formData.end_date) {
      setError('Please select start and end dates');
      return;
    }
    
    if (!formData.total_hours || parseFloat(formData.total_hours) <= 0) {
      setError('Please enter valid hours');
      return;
    }
    
    // Check balance
    const requestedHours = parseFloat(formData.total_hours);
    const availableBalance = balance?.balances[formData.pto_type] || 0;
    
    if (requestedHours > availableBalance) {
      setError(`Insufficient ${formData.pto_type} balance. You have ${availableBalance} hours available.`);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = sessionStorage.getItem('token');
      await axios.post(`${API_URL}/api/pto/requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating PTO request:', err);
      setError(err.response?.data?.detail || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const availableBalance = balance?.balances[formData.pto_type] || 0;
  const remainingAfterRequest = availableBalance - parseFloat(formData.total_hours || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Request Time Off</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* PTO Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Off Type *
            </label>
            <select
              value={formData.pto_type}
              onChange={(e) => setFormData({ ...formData, pto_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="vacation">Vacation</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal Day</option>
            </select>
            {balance && (
              <p className="mt-2 text-sm text-gray-600">
                Available: <span className="font-semibold">{availableBalance} hours</span>
              </p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Optional: Time Range for Partial Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partial Day? (Optional)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                placeholder="Start Time"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                placeholder="End Time"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Leave blank for full days</p>
          </div>

          {/* Total Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Hours *
            </label>
            <input
              type="number"
              step="0.5"
              value={formData.total_hours}
              onChange={(e) => setFormData({ ...formData, total_hours: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            {calculatedHours > 0 && (
              <p className="mt-2 text-sm text-blue-600">
                <Clock className="inline h-4 w-4 mr-1" />
                Auto-calculated: {calculatedHours} hours
              </p>
            )}
            {remainingAfterRequest >= 0 && formData.total_hours && (
              <p className="mt-2 text-sm text-gray-600">
                Remaining after request: <span className="font-semibold">{remainingAfterRequest.toFixed(2)} hours</span>
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="e.g., Family vacation, Doctor appointment"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="Any additional information..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PTORequestModal;
